import { memo, useCallback, useState } from 'react';
import { X, BarChart2, Type, TrendingUp, Code, Layers, Layout } from 'react-feather';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDashboardBuilderEditing } from '@trinity/chat-components/containers/Dashboard/context';

type Props = {
  open: boolean;
  onClose: () => void;
  /** If provided, called with the selected type instead of auto-adding a block */
  onSelect?: (type: string) => void;
  /** When true, the Section tile is hidden (used inside sections to prevent nesting) */
  hideSections?: boolean;
};

type PaletteEntry = {
  type: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ size?: number; color?: string; style?: React.CSSProperties }>;
  disabled?: boolean;
  fullWidth?: boolean;
};

// NOTE: These entries must be kept in sync with the widget types registered in
// createDefaultRegistry() (default-registry.tsx). Adding a widget to the
// registry without adding it here will make it invisible in the palette.
const PALETTE_ENTRIES: PaletteEntry[] = [
  { type: 'chart', label: 'Charts', icon: BarChart2 },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'metric_card', label: 'Metric Card', icon: TrendingUp },
  { type: 'code', label: 'Code', icon: Code },
  { type: 'rep_dashboard', label: 'Territory-Level Insights', icon: Layout },
  {
    type: 'section',
    label: 'Section',
    icon: Layers,
  },
];

/**
 * WidgetPalette — modal widget chooser with a 2×2 tile grid.
 *
 * Shows Charts, Text, Metric Card, Code, and Section options.
 */
export const WidgetPalette = memo(function WidgetPalette({
  open,
  onClose,
  onSelect,
  hideSections = false,
}: Props): React.ReactElement {
  const { addBlockWithWidget } = useDashboardBuilderEditing();
  const [selected, setSelected] = useState<string | null>(null);

  const visibleEntries = hideSections ? PALETTE_ENTRIES.filter((e) => e.type !== 'section') : PALETTE_ENTRIES;

  const handleClose = useCallback(() => {
    setSelected(null);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (!selected) return;
    if (onSelect) {
      onSelect(selected);
    } else {
      addBlockWithWidget(selected, {});
    }
    setSelected(null);
    onClose();
  }, [selected, addBlockWithWidget, onSelect, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, px: 1 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 2, position: 'relative' }}>
        <Typography variant="h6" fontWeight={600}>
          Select Widget to Add
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close"
          sx={{ position: 'absolute', top: 4, right: '-6px', color: 'text.disabled' }}
        >
          <X size={24} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
          {visibleEntries.map((entry) => (
            <WidgetTile
              key={entry.type}
              selected={selected === entry.type}
              disabled={entry.disabled ?? false}
              fullWidth={entry.fullWidth ?? false}
              onClick={() => !entry.disabled && setSelected(entry.type)}
            >
              <entry.icon size={22} color="currentColor" style={{ marginBottom: 8 }} />
              <Typography variant="caption" fontWeight={500} noWrap sx={{ color: 'inherit' }}>
                {entry.label}
              </Typography>
              {entry.description && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'inherit',
                    opacity: 0.7,
                    fontSize: '0.65rem',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    mt: 0.25,
                  }}
                >
                  {entry.description}
                </Typography>
              )}
            </WidgetTile>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <CancelButton variant="outlined" fullWidth onClick={handleClose}>
            Cancel
          </CancelButton>
          <NextButton variant="contained" fullWidth disabled={!selected} onClick={handleNext}>
            Next
          </NextButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

const WidgetTile = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'disabled' && prop !== 'fullWidth',
})<{ selected: boolean; disabled: boolean; fullWidth: boolean }>(
  ({ theme, selected, disabled, fullWidth }) => ({
    '--tile-primary': theme.palette.primary.main,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3, 2),
    gridColumn: fullWidth ? '1 / -1' : undefined,
    border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.grey[200]}`,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    cursor: disabled ? 'default' : 'pointer',
    color: (() => {
      if (disabled) return theme.palette.text.disabled;
      if (selected) return theme.palette.primary.main;
      return theme.palette.text.secondary;
    })(),
    backgroundColor: (() => {
      if (selected) return `${theme.palette.primary.main}14`;
      if (disabled) return theme.palette.action.disabledBackground;
      return theme.palette.background.paper;
    })(),
    transition: 'border-color 0.15s, background-color 0.15s, color 0.15s',
    userSelect: 'none',
    ...(!disabled &&
      !selected && {
        '&:hover': {
          borderColor: theme.palette.primary.main,
          backgroundColor: `${theme.palette.primary.main}08`,
          color: theme.palette.primary.main,
        },
      }),
  })
);

const CancelButton = styled(Button)(({ theme }) => ({
  borderWidth: '2px',
  borderColor: theme.palette.grey[700],
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: 'transparent',
  '&:hover': {
    borderWidth: '2px',
    borderColor: theme.palette.grey[900],
    backgroundColor: theme.palette.grey[200],
  },
}));

const NextButton = styled(Button)(() => ({
  textTransform: 'none',
  fontWeight: 600,
}));
